/* API istemcisi — panelin Worker'la tek konuşma noktası.

   Üç şeyi tek yerde tutuyor ve her biri unutulması kolay olduğu için burada:

     1. **`credentials: 'include'`.** Üretimde panel API'yi kendi kökeninden
        çağırıyor (`admin.frawgraphy.com/api/*`, build.mjs'teki gerekçe) ve
        orada `same-origin` de yeterdi; ama yerelde panel `localhost:8082`,
        API `localhost:8787` — ayrı köken. Varsayılan çerezi orada hiç
        göndermez ve her yönetici çağrısı 404 alır; 404 "kilit reddetti"
        demek olduğu için de hata "uç yok" gibi okunur. Bu, teşhisi en zor
        hâl, o yüzden seçim tek yerde duruyor.
     2. **Hata gövdesinin alan haritası.** Worker `{ error, fields }`
        döndürüyor (packages/shared'daki `errorResponse`). Formlar o haritayı
        doğrudan alanların altına basıyor, yani hata metnini istemci
        uydurmuyor.
     3. **401/404 ayrımı.** Yönetici kilidi bilerek 404 döner (plan §12), yani
        "oturum düştü" ile "böyle bir uç yok" aynı koda düşüyor. İstemci bunu
        oturum kaybı sayıp giriş ekranına dönüyor — yanlış sayfada takılı
        kalan bir panelden iyidir. */

import type { AlbumDetail, AlbumSummary, Design, ErrorResponse, PageSeo, PublishOutput } from '@frawgraphy/shared';
import type { SiteContent } from '@frawgraphy/tools/content';

/** Derleme zamanında gömülür (build.mjs). */
declare const __API_ORIGIN__: string;

export const API_ORIGIN = __API_ORIGIN__;

export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;

  constructor(status: number, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fields = fields;
  }

  /** Kilit reddetti ya da oturum düştü — panel giriş ekranına dönmeli. */
  get isAuthLoss(): boolean {
    return this.status === 404 || this.status === 401;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_ORIGIN}${path}`, {
      method,
      /* Oturum çerezi ayrı kökene ancak böyle gider. */
      credentials: 'include',
      ...(body === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
    });
  } catch (err) {
    /* Ağ hatası ile sunucu hatası ayrı şeyler: biri "internet yok", diğeri
       "sunucu reddetti". İkisini aynı mesaja katlamak teşhisi bozar. */
    throw new ApiError(0, `Sunucuya ulaşılamadı: ${(err as Error).message}`);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* Gövde JSON değilse aşağıda ham metin kullanılır. */
  }

  if (!res.ok) {
    const shaped = parsed as ErrorResponse | null;
    throw new ApiError(
      res.status,
      shaped?.error ?? `İstek reddedildi (${res.status}).`,
      shaped?.fields ?? {}
    );
  }
  return parsed as T;
}

/* ── Oturum ─────────────────────────────────────────────── */

export type Session = { email: string; expiresAt: string };

export const auth = {
  login: (email: string, password: string, totp?: string) =>
    request<Session>('POST', '/api/admin/login', {
      email,
      password,
      ...(totp ? { totp } : {})
    }),
  logout: () => request<void>('POST', '/api/admin/logout'),
  /**
   * Oturumun hâlâ geçerli olup olmadığı.
   *
   * Ayrı bir "ben kimim" ucu yok, o yüzden en ucuz korumalı uç yoklanıyor:
   * cevap gelirse oturum var, 404 gelirse yok. Gereksiz bir uç eklemekten
   * iyi — ve bu çağrı panelin zaten ilk iş olarak çektiği veri.
   */
  probe: () => request<{ albums: AlbumSummary[] }>('GET', '/api/admin/albums')
};

/* ── İçerik ─────────────────────────────────────────────── */

export const albums = {
  list: () => request<{ albums: AlbumSummary[] }>('GET', '/api/admin/albums'),
  read: (id: string) => request<AlbumDetail>('GET', `/api/admin/albums/${encodeURIComponent(id)}`),
  create: (input: Record<string, unknown>) =>
    request<AlbumDetail>('POST', '/api/admin/albums', input),
  patch: (id: string, input: Record<string, unknown>) =>
    request<AlbumDetail>('PATCH', `/api/admin/albums/${encodeURIComponent(id)}`, input),
  remove: (id: string) =>
    request<{ id: string; deleted: boolean }>(
      'DELETE',
      `/api/admin/albums/${encodeURIComponent(id)}`
    ),
  reorder: (id: string, photoIds: string[]) =>
    request<{ id: string; photoIds: string[] }>(
      'POST',
      `/api/admin/albums/${encodeURIComponent(id)}/reorder`,
      { photoIds }
    )
};

export type PhotoRow = {
  id: string;
  width: number;
  height: number;
  ratio: number;
  alt: { tr: string; en?: string };
  bytesBrowse: number | null;
  bytesFull: number | null;
  status: string;
  createdAt: string;
};

export type PhotoPage = { photos: PhotoRow[]; nextBefore: string | null };

export const photos = {
  list: (opts: { before?: string; search?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.before) q.set('before', opts.before);
    if (opts.search) q.set('search', opts.search);
    if (opts.limit) q.set('limit', String(opts.limit));
    const query = q.toString();
    return request<PhotoPage>('GET', `/api/admin/photos${query ? `?${query}` : ''}`);
  },
  commit: (input: Record<string, unknown>) =>
    request<{ id: string; duplicate: boolean; status: string }>(
      'POST',
      '/api/admin/photos',
      input
    )
};

export const publish = {
  run: () => request<PublishOutput>('POST', '/api/admin/publish')
};

export const health = () =>
  request<{ ready: boolean; bindings: Record<string, boolean> }>('GET', '/api/health');

/* ── Sağlık ─────────────────────────────────────────────── */

export type ProbeTarget = 'db' | 'r2' | 'site' | 'notfound';
export type Probe = { target: ProbeTarget; ok: boolean; status: number | null; ms: number; note?: string };

export type HealthLive = {
  probes: Probe[];
  bindings: Record<string, boolean>;
  environment: string;
  at: string;
};

export type TargetHistory = {
  target: ProbeTarget;
  checks: number;
  uptime: number | null;
  p50: number | null;
  p95: number | null;
  series: [number, number, boolean][];
};

export type Ranked = { key: string; count: number };

export type EdgeReport = {
  hours: { hour: string; requests: number; s4xx: number; s5xx: number; cached: number }[];
  workers: { hour: string; requests: number; errors: number; cpuP50: number; cpuP99: number }[];
  security: {
    hourly: { hour: string; events: number }[];
    byAction: Ranked[];
    byIp: Ranked[];
    byCountry: Ranked[];
    byPath: Ranked[];
  };
  baselinePerHour: number;
  alerts: { kind: 'spike' | 'errors' | 'security'; message: string }[];
  cachedAt: string;
};

export const siteHealth = {
  live: () => request<HealthLive>('GET', '/api/admin/health/live'),
  history: (hours = 24) =>
    request<{ hours: number; targets: TargetHistory[] }>('GET', `/api/admin/health/history?hours=${hours}`),
  edge: () => request<EdgeReport>('GET', '/api/admin/health/edge')
};

/* ── Trafik ─────────────────────────────────────────────── */

export type TrafficDay = {
  date: string;
  requests: number;
  pageViews: number;
  uniques: number;
  bytes: number;
  cached: number;
  threats: number;
  visits: number;
  pageloads: number;
};

export type TrafficReport = {
  range: 7 | 30 | 90;
  days: TrafficDay[];
  pages: Ranked[];
  countries: Ranked[];
  rum: boolean;
  rumPaths: Ranked[];
  referrers: Ranked[];
  rumCountries: Ranked[];
  devices: Ranked[];
  vitals: { lcp: number; inp: number; cls: number; samples: number } | null;
  cachedAt: string;
};

export const traffic = (range: 7 | 30 | 90) => request<TrafficReport>('GET', `/api/admin/traffic?range=${range}`);

/* ── Görünüm ────────────────────────────────────────────── */

export type DesignSlot = { doc: Design; updatedAt: string | null; updatedBy: string | null };
export type DesignVersion = { id: number; kind: 'draft' | 'live'; savedAt: string; savedBy: string | null };

export const design = {
  read: () => request<{ draft: DesignSlot; live: DesignSlot | null; dirty: boolean }>('GET', '/api/admin/design'),
  save: (doc: Design) => request<{ updatedAt: string; versionId: number }>('PUT', '/api/admin/design/draft', doc),
  promote: () => request<{ promoted: true; liveUpdatedAt: string }>('POST', '/api/admin/design/promote'),
  versions: () => request<{ versions: DesignVersion[] }>('GET', '/api/admin/design/versions'),
  version: (id: number) => request<{ id: number; kind: string; savedAt: string; doc: Design }>('GET', `/api/admin/design/versions/${id}`),
  content: () => request<SiteContent>('GET', '/api/admin/design/content')
};

/* ── Günlük ─────────────────────────────────────────────── */

type Loc = { tr: string; en?: string };

export type PostRecord = {
  id: string;
  slug: string;
  title: Loc;
  shortTitle: Loc | null;
  titleHtml: Loc | null;
  lede: Loc | null;
  excerpt: Loc | null;
  bodyHtml: Loc | null;
  category: Loc;
  readMinutes: number;
  coverPhotoId: string | null;
  cover: { url: string; alt: Loc; caption?: Loc; w?: number; h?: number } | null;
  seo: PageSeo | null;
  publishedAt: string | null;
  blocks: unknown[] | null;
  blocksDraft: unknown[] | null;
};

export const posts = {
  list: () => request<{ posts: PostRecord[] }>('GET', '/api/admin/posts'),
  read: (id: string) => request<PostRecord>('GET', `/api/admin/posts/${encodeURIComponent(id)}`),
  create: (input: Record<string, unknown>) => request<PostRecord>('POST', '/api/admin/posts', input),
  patch: (id: string, input: Record<string, unknown>) => request<PostRecord>('PATCH', `/api/admin/posts/${encodeURIComponent(id)}`, input),
  remove: (id: string) => request<{ id: string; deleted: boolean }>('DELETE', `/api/admin/posts/${encodeURIComponent(id)}`),
  saveDraft: (id: string, blocks: unknown[]) => request<{ id: string; saved: boolean }>('PUT', `/api/admin/posts/${encodeURIComponent(id)}/draft`, { blocks }),
  promote: (id: string) => request<{ id: string; promoted: boolean }>('POST', `/api/admin/posts/${encodeURIComponent(id)}/promote`)
};
